import { useCallback, useEffect, useState, type ReactNode } from "react";
import { API_BASE } from "@/lib/apiClient";
import { BrandingContext } from "@/lib/branding/BrandingContext";
import { fetchPublicBranding } from "@/lib/branding/fetchBranding";
import type { TenantBranding } from "@/lib/branding/types";
import { applyDocumentFavicon, resolveBrandingAssetUrl } from "@shared/brandingAssetUrl";

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    void fetchPublicBranding()
      .then(setBranding)
      .catch(() => setBranding(null));
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchPublicBranding()
      .then((data) => {
        if (!cancelled) setBranding(data);
      })
      .catch(() => {
        if (!cancelled) setBranding(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!branding) return;
    if (branding.site_name) {
      const prev = document.title;
      if (prev === "Campingplatz-Software" || prev.startsWith("Campingplatz")) {
        document.title = branding.site_name;
      }
    }
    const icon =
      resolveBrandingAssetUrl(branding.favicon_url, API_BASE) ??
      resolveBrandingAssetUrl(branding.logo_url, API_BASE);
    applyDocumentFavicon(icon);
  }, [branding]);

  return (
    <BrandingContext.Provider value={{ branding, loading, reload }}>
      {children}
    </BrandingContext.Provider>
  );
}
